"use client";

import { getUpcomingEvents } from "@/actions/getUpcomingEvents";
import { RADIUS_VALUES } from "@/components/discover/filters/filterConstants";
import type { DiscoverSelection } from "@/components/discover/filters/selection";
import type { EventSource } from "@/types/discover";
import type { Coords } from "@/types/places";
import {
  type ListPage,
  type ListViewport,
  listQueryOptions,
  usePaginatedListQuery,
} from "./paginatedListQuery";

interface UsePaginatedEventsQueryOptions {
  selection: DiscoverSelection;
  /** The selection the server rendered `initialEvents` for. */
  serverSelection: DiscoverSelection;
  location: Coords;
  initialEvents: EventSource[];
  initialTotal: number;
  viewport?: ListViewport;
  pageSize?: number;
  enabled?: boolean;
}

/**
 * Generates a stable query key for paginated events. It holds only the event
 * part of the selection, so a club filter does not refetch the events.
 */
function generateQueryKey(
  selection: DiscoverSelection,
  location: Coords,
): unknown[] {
  const sorted = (values: readonly string[]) => values.slice().sort().join(",");
  return [
    "paginated-events",
    `${location.lat},${location.lon}`,
    sorted(selection.activities),
    selection.date,
    sorted(selection.price),
    sorted(selection.eventAgeGroup),
    sorted(selection.eventSkillLevel),
    selection.eventSort,
    selection.searchQuery,
    RADIUS_VALUES[selection.radius],
  ];
}

type EventsPage = ListPage<"events">;

/** Query key and fetcher for one paginated events list. */
export function eventsQueryOptions({
  selection,
  location,
  viewport,
  pageSize,
}: {
  selection: DiscoverSelection;
  location: Coords;
  viewport?: ListViewport;
  pageSize?: number;
}) {
  return listQueryOptions({
    entity: "events",
    radiusKey: generateQueryKey(selection, location),
    fetchRadiusPage: async (page) => {
      const { events, total, totalRelation } = await getUpcomingEvents(
        selection,
        location,
        page,
      );
      return { items: events, total, totalRelation };
    },
    location,
    viewport,
    pageSize,
  });
}

export function usePaginatedEventsQuery({
  selection,
  serverSelection,
  location,
  initialEvents,
  initialTotal,
  viewport,
  pageSize,
  enabled = true,
}: UsePaginatedEventsQueryOptions) {
  return usePaginatedListQuery({
    entity: "events",
    options: eventsQueryOptions({ selection, location, viewport, pageSize }),
    serverKey: generateQueryKey(serverSelection, location),
    initialItems: initialEvents,
    initialTotal,
    pageSize,
    enabled,
  });
}

/**
 * Helper to flatten paginated events data
 */
export function flattenEventsPages(
  pages: EventsPage[] | undefined,
): EventSource[] {
  if (!pages) return [];
  return pages.flatMap((page) => page.events);
}
